// Custom Collection Filter System
class CustomCollectionFilter {
  constructor() {
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    // Find the filter button
    this.filterButton = document.getElementById('custom-filter-button');
    if (!this.filterButton) return;
    
    // Get the current collection URL and any existing tags
    this.setupCollectionInfo();
    
    // Create the filter drawer and overlay
    this.createFilterDrawer();
    
    // Add event listeners
    this.filterButton.addEventListener('click', this.toggleFilterDrawer.bind(this));
    this.closeButton.addEventListener('click', this.closeFilterDrawer.bind(this));
    this.overlay.addEventListener('click', this.closeFilterDrawer.bind(this));
    
    // Handle ESC key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeFilterDrawer();
    });
    
    // Initialize filter sections with metaobject data
    this.populateFilterOptions();
    
    // Fix tag links to preserve color/style filters
    this.fixTagLinks();
    
    // Fix quick color filters
    this.setupQuickColorFilters();
    
    this.initialized = true;
    console.log('Custom Collection Filter initialized');
  }
  
  setupCollectionInfo() {
    // Get current URL path
    const currentPath = window.location.pathname;
    
    // Parse collection path and any existing tags
    const pathParts = currentPath.split('/');
    
    // Find the collections part
    let collectionsIndex = pathParts.indexOf('collections');
    if (collectionsIndex === -1 && pathParts.length > 1) {
      // Try to find it without the literal 'collections' (might be localized)
      collectionsIndex = 0;
    }
    
    if (collectionsIndex !== -1 && pathParts.length > collectionsIndex + 1) {
      // Get collection handle
      this.collectionHandle = pathParts[collectionsIndex + 1];
      
      // Check if there are tags
      this.existingTags = [];
      if (pathParts.length > collectionsIndex + 2) {
        // Tags are found in the next segment, separated by '+'
        const tagsPart = pathParts[collectionsIndex + 2];
        this.existingTags = tagsPart.split('+');
      }
      
      // Construct base collection URL without tags
      this.collectionUrl = `/collections/${this.collectionHandle}`;
    } else {
      // Default fallback
      this.collectionHandle = 'all';
      this.collectionUrl = '/collections/all';
      this.existingTags = [];
    }
    
    console.log('Collection URL:', this.collectionUrl);
    console.log('Existing tags:', this.existingTags);
    
    // Display active filters on the collection page
    this.displayActiveFiltersOnCollectionPage();
  }

  // New method to display active filters on the collection page
  displayActiveFiltersOnCollectionPage() {
    if (!this.existingTags || this.existingTags.length === 0) {
      return; // No active filters to display
    }
    
    // Get metaobject data to display proper names
    const metaobjectsDataElement = document.getElementById('filter-metaobjects-data');
    let metaobjectsData = null;
    
    if (metaobjectsDataElement) {
      try {
        metaobjectsData = JSON.parse(metaobjectsDataElement.textContent);
      } catch (error) {
        console.error('Error parsing metaobjects data:', error);
      }
    }
    
    // Get tag display text mappings for regular tags
    let tagDisplayMap = {};
    const tagDisplayMapElement = document.getElementById('tag-display-map');
    if (tagDisplayMapElement) {
      try {
        tagDisplayMap = JSON.parse(tagDisplayMapElement.textContent);
      } catch (error) {
        console.warn('Error parsing tag display map:', error);
      }
    }
    
    // Find the product count element to append after
    const productCountElement = document.querySelector('.collection-product-count');
    if (!productCountElement) {
      console.warn('Product count element not found, cannot add active filters to collection page');
      return;
    }
    
    // Create active filters container
    const activeFiltersContainer = document.createElement('div');
    activeFiltersContainer.className = 'collection-active-filters';
    
    // Add each active filter tag
    this.existingTags.forEach(tag => {
      // Skip tags that aren't color or style tags (like "manicure")
      if (!tag.startsWith('color-') && !tag.startsWith('style-')) {
        return;
      }
      
      const filterTag = document.createElement('div');
      filterTag.className = 'active-filter-tag';
      filterTag.setAttribute('data-filter-tag', tag);
      
      // Add removal button (X)
      const removeButton = document.createElement('span');
      removeButton.className = 'filter-remove';
      removeButton.setAttribute('aria-label', 'Remove filter');
      removeButton.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent bubbling
        this.removeSpecificFilter(tag);
      });
      filterTag.appendChild(removeButton);
      
      // Get the display name based on tag type
      let displayText = '';
      
      if (tag.startsWith('color-') || tag.startsWith('style-')) {
        // Handle color and style tags with metaobjects
        if (tag.includes('-')) {
          const [category, ...valueParts] = tag.split('-');
          // Join back all parts after the first dash to handle multi-word values
          const value = valueParts.join('-');
          
          if (metaobjectsData) {
            // Try to find the actual name from the metaobjects
            if (category === 'color' && metaobjectsData.colors) {
              const colorObj = metaobjectsData.colors.find(c => {
                // Try exact match first
                if (c.value === value) return true;
                // Try checking if the URL value is a subset of the metaobject value
                return c.value.includes(value) || value.includes(c.value);
              });
              
              if (colorObj && colorObj.name) {
                displayText = colorObj.name;
              }
            } else if (category === 'style' && metaobjectsData.styles) {
              const styleObj = metaobjectsData.styles.find(s => {
                // Try exact match first
                if (s.value === value) return true;
                // Try checking if the URL value is a subset of the metaobject value
                return s.value.includes(value) || value.includes(s.value);
              });
              
              if (styleObj && styleObj.name) {
                displayText = styleObj.name;
              }
            }
          }
          
          // If we couldn't find the name in metaobjects, fall back to the value from the URL
          if (!displayText) {
            // Capitalize and format the URL value as a fallback
            displayText = value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          }
        }
      } else {
        // For regular tags like "manicure", check the tagDisplayMap
        if (tagDisplayMap[tag]) {
          displayText = tagDisplayMap[tag];
        } else {
          // Fallback to capitalized tag name - preserve hyphens as spaces
          displayText = tag.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        }
      }
      
      // Add text element after the remove button
      const textSpan = document.createElement('span');
      textSpan.className = 'filter-text';
      textSpan.textContent = displayText;
      filterTag.appendChild(textSpan);
      
      activeFiltersContainer.appendChild(filterTag);
    });
    
    // Insert after product count
    productCountElement.parentNode.insertBefore(activeFiltersContainer, productCountElement.nextSibling);
  }
  
  // New method to remove a specific filter tag
  removeSpecificFilter(tagToRemove) {
    if (!tagToRemove || !this.existingTags) return;
    
    // Remove the specific tag from existing tags
    const updatedTags = this.existingTags.filter(tag => tag !== tagToRemove);
    
    // Get existing non-filter tags (e.g., manicure, pedicure)
    const existingCustomTags = this.existingTags.filter(tag => {
      return !tag.startsWith('color-') && !tag.startsWith('style-');
    });
    
    // Build the new URL
    let newUrl = this.collectionUrl;
    
    // If there are still tags, append them to the URL
    const combinedTags = [...updatedTags.filter(tag => tag.startsWith('color-') || tag.startsWith('style-')), ...existingCustomTags];
    
    if (combinedTags.length > 0) {
      newUrl += '/' + combinedTags.join('+');
    }
    
    // Redirect to the new URL
    window.location.href = newUrl;
  }

  createFilterDrawer() {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'custom-filter-drawer-overlay';
    this.overlay.id = 'custom-filter-drawer-overlay';  // Add ID for compatibility with existing scripts
    document.body.appendChild(this.overlay);
    
    // Get customizable text settings from the JSON element
    let textSettings = {
      drawer_title: 'Filter',
      color_title: 'Color',
      effects_title: 'Effects',
      clear_button: 'Clear Filter',
      apply_button: 'Apply Filter'
    };
    
    const textSettingsElement = document.getElementById('filter-text-settings');
    if (textSettingsElement) {
      try {
        const customTexts = JSON.parse(textSettingsElement.textContent);
        textSettings = { ...textSettings, ...customTexts };
      } catch (error) {
        console.warn('Error parsing text settings:', error);
      }
    }
    
    // Create drawer
    this.drawer = document.createElement('div');
    this.drawer.className = 'custom-filter-drawer';
    this.drawer.id = 'custom-filter-drawer';
    
    // Create drawer header
    const header = document.createElement('div');
    header.className = 'custom-filter-drawer-header';
    
    const title = document.createElement('h2');
    title.className = 'custom-filter-drawer-title';
    title.textContent = textSettings.drawer_title;
    
    this.closeButton = document.createElement('button');
    this.closeButton.className = 'custom-filter-drawer-close';
    this.closeButton.id = 'custom-filter-drawer-close';  // Add ID for compatibility with existing scripts
    this.closeButton.innerHTML = '&times;';
    this.closeButton.setAttribute('aria-label', 'Close');
    
    header.appendChild(title);
    header.appendChild(this.closeButton);
    this.drawer.appendChild(header);
    
    // We've moved the active filters to the collection page, so we don't need to display them here anymore
    
    // Create content container for the filters
    this.filterContent = document.createElement('div');
    this.filterContent.className = 'custom-filter-content';
    
    // Create Color filter section
    const colorFilterSection = document.createElement('div');
    colorFilterSection.className = 'filter-section';
    const colorHeading = document.createElement('h3');
    colorHeading.className = 'filter-section-title';
    colorHeading.textContent = textSettings.color_title;
    
    const colorGrid = document.createElement('div');
    colorGrid.className = 'filter-option-grid color-filter-grid';
    colorGrid.id = 'color-filter-grid';
    
    colorFilterSection.appendChild(colorHeading);
    colorFilterSection.appendChild(colorGrid);
    
    // Create Effects filter section
    const effectsFilterSection = document.createElement('div');
    effectsFilterSection.className = 'filter-section';
    const effectsHeading = document.createElement('h3');
    effectsHeading.className = 'filter-section-title';
    effectsHeading.textContent = textSettings.effects_title;
    
    const effectsGrid = document.createElement('div');
    effectsGrid.className = 'filter-option-grid effects-filter-grid';
    effectsGrid.id = 'effects-filter-grid';
    
    effectsFilterSection.appendChild(effectsHeading);
    effectsFilterSection.appendChild(effectsGrid);
    
    // Add sections to content
    this.filterContent.appendChild(colorFilterSection);
    this.filterContent.appendChild(effectsFilterSection);
    
    this.drawer.appendChild(this.filterContent);
    
    // Add filter action buttons
    const actionContainer = document.createElement('div');
    actionContainer.className = 'filter-action-buttons';
    
    const clearButton = document.createElement('button');
    clearButton.className = 'filter-action-button clear-button';
    clearButton.textContent = textSettings.clear_button;
    clearButton.addEventListener('click', this.clearAllFilters.bind(this));
    
    const applyButton = document.createElement('button');
    applyButton.className = 'filter-action-button apply-button';
    applyButton.textContent = textSettings.apply_button;
    applyButton.addEventListener('click', this.applyFilters.bind(this));
    
    actionContainer.appendChild(clearButton);
    actionContainer.appendChild(applyButton);
    
    this.drawer.appendChild(actionContainer);
    
    // Add selected filters section
    this.selectedFilters = document.createElement('div');
    this.selectedFilters.className = 'custom-filter-selected';
    this.drawer.appendChild(this.selectedFilters);
    
    document.body.appendChild(this.drawer);
  }
  
  populateFilterOptions() {
    // Get metaobject data from the JSON script
    const metaobjectsDataElement = document.getElementById('filter-metaobjects-data');
    
    if (metaobjectsDataElement) {
      try {
        const metaobjectsData = JSON.parse(metaobjectsDataElement.textContent);
        
        // Add color filter options from metaobjects
        this.populateColorOptions(metaobjectsData.colors);
        
        // Add style/effect filter options from metaobjects
        this.populateEffectOptions(metaobjectsData.styles);
      } catch (error) {
        console.error('Error parsing metaobjects data:', error);
        // Fallback to default options if JSON parsing fails
        this.populateDefaultOptions();
      }
    } else {
      console.warn('Metaobjects data element not found, using default options');
      // Fallback to default options if the JSON element is missing
      this.populateDefaultOptions();
    }
  }
  
  populateColorOptions(colors) {
    const colorGrid = this.drawer.querySelector('#color-filter-grid');
    if (!colorGrid || !colors || !colors.length) {
      console.warn('Color grid element not found or no color data available');
      return;
    }
    
    colors.forEach(color => {
      // Create filter tag for color (e.g., color-red)
      const filterTag = `color-${color.value}`;
      
      // Check if this color is already selected in the existing tags
      const isActive = this.existingTags.includes(filterTag);
      
      const option = this.createFilterOption(
        color.name, 
        filterTag, 
        'color', 
        color.hex,
        color.image,
        isActive
      );
      colorGrid.appendChild(option);
    });
  }
  
  populateEffectOptions(styles) {
    const effectsGrid = this.drawer.querySelector('#effects-filter-grid');
    if (!effectsGrid || !styles || !styles.length) {
      console.warn('Effects grid element not found or no style data available');
      return;
    }
    
    styles.forEach(style => {
      // Create filter tag for effect (e.g., style-glitter instead of effect-glitter)
      const filterTag = `style-${style.value}`;
      
      // Check if this effect is already selected in the existing tags
      const isActive = this.existingTags.includes(filterTag);
      
      const option = this.createFilterOption(
        style.name, 
        filterTag,
        'effect', // Keep 'effect' as the type for internal consistency
        null,
        style.image,
        isActive
      );
      effectsGrid.appendChild(option);
    });
  }
  
  // Fallback method to populate default options if metaobjects aren't available
  populateDefaultOptions() {
    // Add color filter options
    const colorGrid = this.drawer.querySelector('#color-filter-grid');
    if (colorGrid) {
      const colors = [
        { name: 'Red', hex: '#e53935', value: 'color-red' },
        { name: 'Nude', hex: '#f5d6c6', value: 'color-nude' },
        { name: 'Green', hex: '#43a047', value: 'color-green' },
        { name: 'Purple', hex: '#8e24aa', value: 'color-purple' },
        { name: 'Blue', hex: '#1e88e5', value: 'color-blue' }
      ];
      
      colors.forEach(color => {
        // Check if this color is already selected in the existing tags
        const isActive = this.existingTags.includes(color.value);
        
        const option = this.createFilterOption(color.name, color.value, 'color', color.hex, null, isActive);
        colorGrid.appendChild(option);
      });
    }
    
    // Add effects filter options
    const effectsGrid = this.drawer.querySelector('#effects-filter-grid');
    if (effectsGrid) {
      const effects = [
        { name: 'Solid', value: 'style-solid' },
        { name: 'Glitter', value: 'style-glitter' },
        { name: 'Pattern', value: 'style-pattern' },
        { name: 'Metallic', value: 'style-metallic' },
        { name: 'French', value: 'style-french' }
      ];
      
      effects.forEach(effect => {
        // Check if this effect is already selected in the existing tags
        const isActive = this.existingTags.includes(effect.value);
        
        const option = this.createFilterOption(effect.name, effect.value, 'effect', null, null, isActive);
        effectsGrid.appendChild(option);
      });
    }
  }
  
  createFilterOption(name, value, type, color = null, imageUrl = null, isActive = false) {
    const option = document.createElement('div');
    option.className = 'filter-option';
    option.setAttribute('data-filter-type', type);
    option.setAttribute('data-filter-value', value);
    
    const button = document.createElement('button');
    button.className = 'filter-option-button';
    if (isActive) {
      button.classList.add('active');
    }
    button.setAttribute('type', 'button');
    
    // Create icon element for color or effect
    const icon = document.createElement('span');
    
    if (type === 'color') {
      icon.className = 'color-swatch';
      if (imageUrl) {
        // Use image for the color if available
        icon.style.backgroundImage = `url(${imageUrl})`;
        icon.style.backgroundSize = 'cover';
        icon.style.backgroundPosition = 'center';
      } else if (color) {
        // Use hex color otherwise
        icon.style.backgroundColor = color;
      }
    } else {
      icon.className = 'effect-icon';
      if (imageUrl) {
        // Use image for the effect if available
        icon.style.backgroundImage = `url(${imageUrl})`;
        icon.style.backgroundSize = 'cover';
        icon.style.backgroundPosition = 'center';
      }
    }
    
    const label = document.createElement('span');
    label.className = 'filter-option-label';
    label.textContent = name;
    
    button.appendChild(icon);
    button.appendChild(label);
    
    option.appendChild(button);
    
    // Single selection behavior
    button.addEventListener('click', () => {
      const isCurrentlyActive = button.classList.contains('active');
      
      // If already active, do nothing special (allow toggling off)
      if (isCurrentlyActive) {
        button.classList.remove('active');
        this.handleFilterChange(type, value, false);
        return;
      }
      
      // Otherwise, deselect all options of the same type first
      if (type === 'color') {
        const allColorOptions = document.querySelectorAll('.filter-option[data-filter-type="color"] .filter-option-button');
        allColorOptions.forEach(btn => {
          btn.classList.remove('active');
          // Get the value from the parent
          const filterValue = btn.closest('.filter-option').getAttribute('data-filter-value');
          if (filterValue !== value) {
            this.handleFilterChange('color', filterValue, false);
          }
        });
      } else if (type === 'effect') {
        const allEffectOptions = document.querySelectorAll('.filter-option[data-filter-type="effect"] .filter-option-button');
        allEffectOptions.forEach(btn => {
          btn.classList.remove('active');
          // Get the value from the parent
          const filterValue = btn.closest('.filter-option').getAttribute('data-filter-value');
          if (filterValue !== value) {
            this.handleFilterChange('effect', filterValue, false);
          }
        });
      }
      
      // Then activate the clicked option
      button.classList.add('active');
      this.handleFilterChange(type, value, true);
    });
    
    return option;
  }
  
  handleFilterChange(type, value, isActive) {
    console.log(`Filter ${type}:${value} is now ${isActive ? 'active' : 'inactive'}`);
    // In a real implementation, this would filter the products based on selected filters
    // For now, just a placeholder that logs the filter state
  }
  
  clearAllFilters() {
    // Get all filter option buttons
    const allFilterButtons = this.drawer.querySelectorAll('.filter-option-button');
    
    // Remove active class from all buttons
    allFilterButtons.forEach(button => {
      button.classList.remove('active');
    });
    
    // Clear selected filters array but don't navigate away
    this.selectedFilters = [];
    
    // Also clear any filters from the URLs in our internal tracking
    this.selectedColorFilter = null;
    this.selectedStyleFilter = null;
    
    console.log('Filters cleared in drawer (without navigation)');
  }
  
  /**
   * Applies the selected filters by redirecting to the URL with tag parameters
   */
  applyFilters() {
    // Get all selected filter tags
    const selectedTags = [];
    
    this.drawer.querySelectorAll('.filter-option-button.active').forEach(button => {
      const option = button.closest('.filter-option');
      const filterValue = option.getAttribute('data-filter-value');
      selectedTags.push(filterValue);
    });
    
    console.log('Selected filter tags:', selectedTags);
    
    // Get existing non-color and non-effect tags (e.g., manicure, pedicure)
    const existingCustomTags = this.existingTags.filter(tag => {
      return !tag.startsWith('color-') && !tag.startsWith('style-');
    });
    
    // Combine existing custom tags with new selected tags
    const allTags = [...existingCustomTags, ...selectedTags];
    
    if (allTags.length === 0) {
      // If no tags, go to the base collection URL
      window.location.href = this.collectionUrl;
    } else {
      // Create the tag part of the URL
      const tagPart = allTags.join('+');
      const filterUrl = `${this.collectionUrl}/${tagPart}`;
      
      // Redirect to the filtered URL
      window.location.href = filterUrl;
    }
  }

  toggleFilterDrawer() {
    if (this.drawer.classList.contains('active') || this.drawer.classList.contains('open')) {
      this.closeFilterDrawer();
    } else {
      this.openFilterDrawer();
    }
  }

  openFilterDrawer() {
    // First make sure the drawer is visible in the DOM
    this.drawer.style.display = 'block';
    this.overlay.style.display = 'block';
    
    // Small timeout to ensure the transition works
    setTimeout(() => {
      this.drawer.classList.add('active');
      this.drawer.classList.add('open'); // Add open class for compatibility
      this.overlay.classList.add('active');
      this.overlay.classList.add('open'); // Add open class for compatibility
      document.body.style.overflow = 'hidden';
    }, 10);
  }

  closeFilterDrawer() {
    this.drawer.classList.remove('active');
    this.drawer.classList.remove('open'); // Remove open class for compatibility
    this.overlay.classList.remove('active');
    this.overlay.classList.remove('open'); // Remove open class for compatibility
    document.body.style.overflow = '';
    
    // Hide the elements after the transition completes
    setTimeout(() => {
      this.drawer.style.display = 'none';
      this.overlay.style.display = 'none';
    }, 300);
  }
  
  // New method to fix tag links
  fixTagLinks() {
    // Get all tag links in the collection
    const tagLinks = document.querySelectorAll('.collection-tag-filters .tag-link');
    
    // Skip if no tag links found
    if (!tagLinks.length) return;
    
    // Extract existing color and style tags
    const colorTags = this.existingTags.filter(tag => tag.startsWith('color-'));
    const styleTags = this.existingTags.filter(tag => tag.startsWith('style-'));
    
    // Only proceed if we have color or style tags
    if (colorTags.length === 0 && styleTags.length === 0) return;
    
    // Get all tags that should be preserved (color and style)
    const tagsToPreserve = [...colorTags, ...styleTags];
    
    // Update each tag link to include existing color and style filters
    tagLinks.forEach(link => {
      const href = link.getAttribute('href');
      
      // Skip the 'All' link, which should clear all tags
      if (href === this.collectionUrl) return;
      
      // Skip links that already contain preserved tags (these are deselection links)
      if (tagsToPreserve.some(tag => href.includes(tag))) return;
      
      // Extract the tag from the href
      const hrefParts = href.split('/');
      const tagPart = hrefParts[hrefParts.length - 1];
      
      // Check if this tag link already contains a '+' (multiple tags)
      if (tagPart.includes('+')) return;
      
      // Create a new URL with the current tag and preserved tags
      let newUrl = href;
      if (tagsToPreserve.length > 0) {
        newUrl = `${href}+${tagsToPreserve.join('+')}`;
      }
      
      // Update the link's href
      link.setAttribute('href', newUrl);
    });
  }
  
  // New method to handle quick color filters
  setupQuickColorFilters() {
    const colorFilterLinks = document.querySelectorAll('.collection-quick-color-filters .color-filter-link');
    if (!colorFilterLinks.length) return;
    
    colorFilterLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        const colorTag = link.getAttribute('title').toLowerCase();
        const formattedColorTag = 'color-' + colorTag.replace(/\s+/g, '-');
        const isActive = link.parentElement.classList.contains('active');
        
        // Get all current tags except the color tags
        const currentTags = this.existingTags.filter(tag => !tag.startsWith('color-'));
        
        // If this color is not active, add it
        if (!isActive) {
          currentTags.push(formattedColorTag);
        }
        
        // Build the new URL
        let newUrl = this.collectionUrl;
        if (currentTags.length > 0) {
          newUrl += '/' + currentTags.join('+');
        }
        
        // Navigate to the new URL
        window.location.href = newUrl;
      });
    });
  }
}

// Initialize the filter when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Remove any existing script-created filter drawers to avoid duplication
  const existingOverlay = document.getElementById('custom-filter-drawer-overlay');
  const existingDrawer = document.getElementById('custom-filter-drawer');
  
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  if (existingDrawer) {
    existingDrawer.remove();
  }
  
  window.customFilter = new CustomCollectionFilter();
});

// Re-initialize when sections load in Shopify editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    window.customFilter = new CustomCollectionFilter();
  });
}
